import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Typography,
  Slider,
  Button,
  ButtonGroup,
  Stack,
  Chip,
  FormHelperText,
  LinearProgress,
  Avatar,
  Paper,
  Alert,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  SchoolOutlined,
  BedOutlined,
  FitnessCenterOutlined,
  AutoStoriesOutlined,
  PsychologyOutlined,
  PeopleOutlined,
  FamilyRestroomOutlined,
  CheckCircleRounded,
} from "@mui/icons-material";

// ─── Scale labels for button groups ──────────────────────────────────────────
const scaleLabels = ["None", "Low", "Medium", "High"];
const valueToLabel = (val) => scaleLabels[val] ?? String(val);

// ─── Default values ───────────────────────────────────────────────────────────
const initialValues = {
  study_hours:    2,
  sleep_hours:    7.5,
  effort_score:   5,
  tutor_sessions: 0,
  parental_help:  1,
  sports_hours:   0,
  peer_influence: 1,
};

const validationSchema = Yup.object({
  study_hours:    Yup.number().min(0).max(24).required(),
  sleep_hours:    Yup.number().min(0).max(12).required(),
  effort_score:   Yup.number().integer().min(0).max(10).required(),
  tutor_sessions: Yup.number().integer().min(0).max(10).required(),
  parental_help:  Yup.number().integer().oneOf([0, 1, 2, 3]).required(),
  sports_hours:   Yup.number().integer().min(0).max(20).required(),
  peer_influence: Yup.number().integer().oneOf([0, 1, 2, 3]).required(),
});

// ─── Count how many fields differ from defaults ───────────────────────────────
function countChanges(values) {
  return Object.keys(initialValues).filter(
    (key) => values[key] !== initialValues[key]
  ).length;
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ icon: Icon, iconColor, iconBg, label, children, changed }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1,
        p: 2,
        borderColor: changed ? 'secondary.light' : 'divider',
        transition: 'border-color 0.2s ease',
        position: 'relative',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.75 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1,
          bgcolor: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon sx={{ fontSize: 17, color: iconColor }} />
        </Box>
        <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
        {changed && (
          <CheckCircleRounded sx={{ fontSize: 15, color: 'secondary.main', ml: 'auto' }} />
        )}
      </Stack>
      {children}
    </Paper>
  );
}

// ─── Slider field ─────────────────────────────────────────────────────────────
function SliderField({ value, onChange, min, max, step, marks, chipLabel, error }) {
  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {min} → {max}
        </Typography>
        <Chip
          label={chipLabel}
          size="small"
          color="secondary"
          variant="outlined"
          sx={{ height: 22, fontSize: '0.72rem', fontWeight: 700, borderRadius: 1 }}
        />
      </Stack>
      <Slider
        value={value}
        onChange={(_, val) => onChange(val)}
        min={min}
        max={max}
        step={step}
        color="secondary"
        valueLabelDisplay="auto"
        marks={marks}
        sx={{ '& .MuiSlider-markLabel': { fontSize: '0.65rem' } }}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Stack>
  );
}

// ─── Button group field ───────────────────────────────────────────────────────
function ScaleButtons({ value, onChange, error }) {
  return (
    <Stack spacing={0.75}>
      <ButtonGroup fullWidth size="small" variant="outlined">
        {[0, 1, 2, 3].map((v) => (
          <Button
            key={v}
            onClick={() => onChange(v)}
            variant={value === v ? 'contained' : 'outlined'}
            color="secondary"
            sx={{
              textTransform: 'none',
              fontWeight: value === v ? 700 : 400,
              fontSize: '0.78rem',
              borderRadius: v === 0 ? '4px 0 0 4px' : v === 3 ? '0 4px 4px 0' : 0,
            }}
          >
            {valueToLabel(v)}
          </Button>
        ))}
      </ButtonGroup>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Stack>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function StudentPreAssessmentForm({ onSubmit, student }) {
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(vals, { setSubmitting }) => {
        const payload = {
          ...vals,
          effort_score:   Math.round(vals.effort_score),
          tutor_sessions: Math.round(vals.tutor_sessions),
          sports_hours:   Math.round(vals.sports_hours),
          study_hours:    Math.round(vals.study_hours),
        };
        onSubmit?.(payload);
        setSubmitting(false);
      }}
    >
      {({ values, errors, touched, handleSubmit, setFieldValue, isSubmitting, isValid, resetForm }) => {
        const changesCount   = countChanges(values);
        const MIN_CHANGES    = 2;
        const canSubmit      = changesCount >= MIN_CHANGES && isValid && !isSubmitting;
        const progress       = Math.min((changesCount / MIN_CHANGES) * 100, 100);

        return (
          <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 620, mx: 'auto', px: { xs: 1, sm: 0 } }}>
            <Card variant="outlined" sx={{ borderRadius: 1 }}>

              {/* ── Header ── */}
              <Box sx={{ px: 3, pt: 3, pb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: '#E6F1FB' }}>
                    <SchoolOutlined sx={{ color: '#185FA5', fontSize: 22 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                      Pre-assessment check-in
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Answer honestly — this helps us support you better
                    </Typography>
                  </Box>
                </Stack>

                {student && (
                  <Chip
                    size="small"
                    label={`${student.first_name} ${student.middle_name ?? ''} ${student.last_name}`.trim()}
                    sx={{ borderRadius: 1, fontWeight: 600, bgcolor: 'action.hover', mt: 0.5 }}
                  />
                )}
              </Box>

              <Divider />

              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={1}>

                  {/* ── Study ── */}
                  <Section
                    icon={AutoStoriesOutlined} iconBg="#E1F5EE" iconColor="#0F6E56"
                    label="Study hours (per night)"
                    changed={values.study_hours !== initialValues.study_hours}
                  >
                    <SliderField
                      value={values.study_hours}
                      onChange={(v) => setFieldValue('study_hours', v)}
                      min={0} max={24} step={0.5}
                      chipLabel={`${values.study_hours.toFixed(1)} hrs`}
                      marks={[{ value: 0, label: '0' }, { value: 6, label: '6' }, { value: 12, label: '12' }, { value: 24, label: '24' }]}
                      error={touched.study_hours && errors.study_hours}
                    />
                  </Section>

                  {/* ── Sleep ── */}
                  <Section
                    icon={BedOutlined} iconBg="#E8EAF6" iconColor="#3949AB"
                    label="Sleep hours (per night)"
                    changed={values.sleep_hours !== initialValues.sleep_hours}
                  >
                    <SliderField
                      value={values.sleep_hours}
                      onChange={(v) => setFieldValue('sleep_hours', v)}
                      min={0} max={12} step={0.5}
                      chipLabel={`${values.sleep_hours.toFixed(1)} hrs`}
                      marks={[{ value: 0, label: '0' }, { value: 6, label: '6' }, { value: 8, label: '8' }, { value: 12, label: '12' }]}
                      error={touched.sleep_hours && errors.sleep_hours}
                    />
                  </Section>

                  {/* ── Effort ── */}
                  <Section
                    icon={PsychologyOutlined} iconBg="#FAECE7" iconColor="#993C1D"
                    label="Effort preparing for this assessment (0–10)"
                    changed={values.effort_score !== initialValues.effort_score}
                  >
                    <SliderField
                      value={values.effort_score}
                      onChange={(v) => setFieldValue('effort_score', Math.round(v))}
                      min={0} max={10} step={1}
                      chipLabel={`${Math.round(values.effort_score)} / 10`}
                      marks={[{ value: 0, label: '0' }, { value: 5, label: '5' }, { value: 10, label: '10' }]}
                      error={touched.effort_score && errors.effort_score}
                    />
                  </Section>

                  {/* ── Tutor sessions ── */}
                  <Section
                    icon={SchoolOutlined} iconBg="#E0F7FA" iconColor="#00838F"
                    label="Tutoring sessions (per week)"
                    changed={values.tutor_sessions !== initialValues.tutor_sessions}
                  >
                    <SliderField
                      value={values.tutor_sessions}
                      onChange={(v) => setFieldValue('tutor_sessions', Math.round(v))}
                      min={0} max={10} step={1}
                      chipLabel={`${values.tutor_sessions}× / week`}
                      marks={[{ value: 0, label: '0' }, { value: 5, label: '5' }, { value: 10, label: '10' }]}
                      error={touched.tutor_sessions && errors.tutor_sessions}
                    />
                  </Section>

                  {/* ── Sports ── */}
                  <Section
                    icon={FitnessCenterOutlined} iconBg="#FAEEDA" iconColor="#854F0B"
                    label="Sports / physical activity (hrs per week)"
                    changed={values.sports_hours !== initialValues.sports_hours}
                  >
                    <SliderField
                      value={values.sports_hours}
                      onChange={(v) => setFieldValue('sports_hours', Math.round(v))}
                      min={0} max={20} step={1}
                      chipLabel={`${values.sports_hours} hrs`}
                      marks={[{ value: 0, label: '0' }, { value: 10, label: '10' }, { value: 20, label: '20' }]}
                      error={touched.sports_hours && errors.sports_hours}
                    />
                  </Section>

                  {/* ── Parental help & Peer influence ── */}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Section
                      icon={FamilyRestroomOutlined} iconBg="#EEEDFE" iconColor="#534AB7"
                      label="Parental / guardian help"
                      changed={values.parental_help !== initialValues.parental_help}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        How often do you get help at home?
                      </Typography>
                      <ScaleButtons
                        value={values.parental_help}
                        onChange={(v) => setFieldValue('parental_help', v)}
                        error={touched.parental_help && errors.parental_help}
                      />
                    </Section>

                    <Section
                      icon={PeopleOutlined} iconBg="#E8F5E9" iconColor="#2E7D32"
                      label="Peer influence"
                      changed={values.peer_influence !== initialValues.peer_influence}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        How much do friends / peers help you?
                      </Typography>
                      <ScaleButtons
                        value={values.peer_influence}
                        onChange={(v) => setFieldValue('peer_influence', v)}
                        error={touched.peer_influence && errors.peer_influence}
                      />
                    </Section>
                  </Stack>

                </Stack>
              </CardContent>

              <Divider />

              {/* ── Footer ── */}
              <Box sx={{ px: 3, py: 2 }}>
                {/* Progress towards minimum changes */}
                {changesCount < MIN_CHANGES && (
                  <Box sx={{ mb: 2 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                      <Typography variant="caption" color="text.secondary">
                        Please adjust at least {MIN_CHANGES} answers before submitting
                      </Typography>
                      <Typography variant="caption" fontWeight={700} color="secondary.main">
                        {changesCount} / {MIN_CHANGES}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      color="secondary"
                      sx={{ borderRadius: 1, height: 4 }}
                    />
                  </Box>
                )}

                {changesCount >= MIN_CHANGES && (
                  <Alert severity="success" variant="outlined" icon={<CheckCircleRounded fontSize="small" />}
                    sx={{ mb: 2, borderRadius: 1, py: 0.5, fontSize: '0.82rem' }}>
                    Looks good — you're ready to submit.
                  </Alert>
                )}

                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button
                    onClick={() => resetForm()}
                    variant="text"
                    sx={{ borderRadius: 1, textTransform: 'none', color: 'text.secondary' }}
                  >
                    Reset
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    disabled={!canSubmit}
                    sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 700, minWidth: 120 }}
                  >
                    {isSubmitting ? 'Submitting…' : 'Submit'}
                  </Button>
                </Stack>
              </Box>
            </Card>
          </Box>
        );
      }}
    </Formik>
  );
}